import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { useAuth } from './AuthContext';
import { apiUploadSource } from './api';
import { MiniSpinner } from './PageLoader';
import { FileTreeView } from './FileTree';

const UPLOAD_STATES = { idle: 'idle', uploading: 'uploading', done: 'done', error: 'error' };

function buildTree(paths) {
  const root = { name: '', type: 'folder', children: [] };
  for (const p of paths) {
    const parts = p.replace(/\/$/, '').split('/');
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;
      let child = node.children.find(c => c.name === part);
      if (!child) {
        const isLast = i === parts.length - 1;
        const isFolder = p.endsWith('/') ? isLast : !isLast;
        child = { name: part, type: isFolder || !isLast ? 'folder' : 'file', children: [] };
        node.children.push(child);
      }
      node = child;
    }
  }
  function sort(n) {
    n.children.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    n.children.forEach(sort);
  }
  sort(root);
  return root;
}

function fileIcon(name) {
  const ext = name.split('.').pop().toLowerCase();
  const map = {
    js: '🟨', ts: '🟦', tsx: '🟦', jsx: '🟨', json: '📋',
    html: '🌐', css: '🎨', scss: '🎨', less: '🎨',
    md: '📝', txt: '📄', xml: '📄', yaml: '📄', yml: '📄',
    png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️', svg: '🖼️', ico: '🖼️',
    zip: '🗜️', gz: '🗜️', tar: '🗜️',
    pdf: '📕', sh: '⚙️', bat: '⚙️', env: '⚙️', config: '⚙️',
    sql: '🗄️', py: '🐍', java: '☕', cs: '💠', cpp: '💠', c: '💠',
  };
  return map[ext] ?? '📄';
}

function TreeNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const isFolder = node.type === 'folder';
  if (isFolder && node.children.length === 0 && depth === 0) return null;
  return (
    <div>
      <div
        style={{ ...treeStyles.row, paddingLeft: 8 + depth * 16, cursor: isFolder ? 'pointer' : 'default' }}
        onClick={() => isFolder && setOpen(o => !o)}
      >
        <span style={treeStyles.arrow}>{isFolder ? (open ? '▾' : '▸') : ' '}</span>
        <span style={treeStyles.icon}>{isFolder ? (open ? '📂' : '📁') : fileIcon(node.name)}</span>
        <span style={{ ...treeStyles.label, fontWeight: isFolder ? 600 : 400 }}>{node.name}</span>
      </div>
      {isFolder && open && node.children.map(c => (
        <TreeNode key={c.name} node={c} depth={depth + 1} />
      ))}
    </div>
  );
}

export default function UploadPage() {
  const { project } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [file,        setFile]        = useState(null);
  const [zipTree,     setZipTree]     = useState(null);
  const [zipError,    setZipError]    = useState('');
  const [zipStats,    setZipStats]    = useState(null);
  const [status,      setStatus]      = useState(UPLOAD_STATES.idle);
  const [progress,    setProgress]    = useState(0);
  const [result,      setResult]      = useState(null);
  const [uploadError, setUploadError] = useState('');

  async function parseZip(picked) {
    setZipTree(null);
    setZipError('');
    setZipStats(null);
    try {
      const zip = await JSZip.loadAsync(picked);
      const paths = Object.keys(zip.files);
      const tree = buildTree(paths);
      let fileCount = 0, folderCount = 0, totalSize = 0;
      zip.forEach((_, entry) => {
        if (entry.dir) folderCount++;
        else {
          fileCount++;
          totalSize += entry._data?.uncompressedSize ?? entry._data?.compressedSize ?? 0;
        }
      });
      setZipStats({ files: fileCount, folders: folderCount, totalSize });
      setZipTree(tree);
    } catch {
      setZipError('Could not read ZIP file. Make sure it is a valid archive.');
    }
  }

  async function handleFileChange(e) {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    setStatus(UPLOAD_STATES.idle);
    setResult(null);
    setUploadError('');
    if (picked && picked.name.toLowerCase().endsWith('.zip')) {
      await parseZip(picked);
    } else {
      setZipTree(null);
      setZipStats(null);
    }
  }

  function handleRemoveFile() {
    setFile(null);
    setZipTree(null);
    setZipStats(null);
    setZipError('');
    setStatus(UPLOAD_STATES.idle);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleUpload() {
    if (!file) { setUploadError('Please select a ZIP file.'); return; }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setUploadError('Only ZIP files are accepted.');
      return;
    }

    setUploadError('');
    setStatus(UPLOAD_STATES.uploading);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(p => Math.min(p + Math.random() * 18, 90));
    }, 200);

    try {
      const res = await apiUploadSource(project.id, file);
      clearInterval(interval);
      setProgress(100);
      setResult(res);
      setStatus(UPLOAD_STATES.done);
    } catch (err) {
      clearInterval(interval);
      setUploadError(err.message || 'Upload failed. Please try again.');
      setStatus(UPLOAD_STATES.error);
    }
  }

  function handleReset() {
    setFile(null);
    setZipTree(null);
    setZipStats(null);
    setZipError('');
    setStatus(UPLOAD_STATES.idle);
    setProgress(0);
    setResult(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const uploading         = status === UPLOAD_STATES.uploading;
  const done              = status === UPLOAD_STATES.done;
  const showPreviewTree   = zipTree && !done;
  const showExtractedTree = done && zipTree;

  return (
    <div className="customer-window">
      <div className="window-header">
        <div className="window-header-icon">⬆️</div>
        <div className="window-header-text">
          <h1>Upload Source Code</h1>
          <p>{project.name} — upload a ZIP archive into the project source folder</p>
        </div>
      </div>

      <div className="window-body">
        <div className="two-col-layout">

          {/* ── Left: Form ─────────────────────────── */}
          <div className="two-col-main">

            {/* ZIP file */}
            <div className="card" style={styles.stepCard}>
              <div style={styles.stepHeader}>
                <span style={styles.stepNum}>1</span>
                <div>
                  <div style={styles.stepTitle}>ZIP Archive</div>
                  <div style={styles.stepSub}>Select a .zip file — contents will be previewed and extracted on upload</div>
                </div>
              </div>

              <div
                style={styles.dropZone}
                onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
              >
                <span style={{ fontSize: 28 }}>🗜️</span>
                <span style={styles.dropLabel}>
                  {file ? file.name : 'Click to browse for a ZIP file'}
                </span>
                <span style={styles.dropSub}>
                  {file ? formatBytes(file.size) : 'Only .zip archives are accepted'}
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={uploading}
              />

              {file && (
                <div style={styles.fileRow}>
                  <span style={styles.fileIcon}>📦</span>
                  <span style={styles.fileName}>{file.name}</span>
                  <span style={styles.fileSize}>{formatBytes(file.size)}</span>
                  <button
                    style={styles.removeBtn}
                    onClick={handleRemoveFile}
                    disabled={uploading}
                    aria-label="Remove"
                  >×</button>
                </div>
              )}

              {zipError && <div style={styles.zipError}>{zipError}</div>}
            </div>

            {/* Error */}
            {uploadError && (
              <div className="error-alert" style={{ borderRadius: 'var(--radius-sm)' }}>
                <span>{uploadError}</span>
                <button className="alert-close" onClick={() => setUploadError('')}>×</button>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center', padding: '11px 16px', fontWeight: 600 }}
                onClick={handleUpload}
                disabled={uploading || done}
              >
                {uploading ? <><MiniSpinner size={16} /> Uploading…</> : '⬆️  Upload Now'}
              </button>
              {(file || done) && (
                <button className="btn btn-secondary" onClick={handleReset} disabled={uploading}>
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* ── Right: preview / status / extracted tree ── */}
          <div className="two-col-aside">

            {/* ZIP tree preview (before upload) */}
            {showPreviewTree && (
              <div className="card" style={styles.treeCard}>
                <div style={styles.treeHeader}>
                  <span style={{ fontSize: 16 }}>🗜️</span>
                  <span style={styles.treeTitle}>ZIP Contents</span>
                  {zipStats && (
                    <span style={styles.treeStats}>
                      {zipStats.files} file{zipStats.files !== 1 ? 's' : ''}
                      {zipStats.folders > 0 ? `, ${zipStats.folders} folder${zipStats.folders !== 1 ? 's' : ''}` : ''}
                      {zipStats.totalSize > 0 ? ` · ${formatBytes(zipStats.totalSize)} uncompressed` : ''}
                    </span>
                  )}
                </div>
                <div style={styles.treeScroll}>
                  {zipTree.children.map(c => (
                    <TreeNode key={c.name} node={c} depth={0} />
                  ))}
                </div>
              </div>
            )}

            {/* Upload status (while not done) */}
            {!done && (
              <div className="card" style={styles.statusCard}>
                <div style={styles.statusTitle}>Upload Status</div>

                {status === UPLOAD_STATES.idle && (
                  <div style={styles.idleState}>
                    <span style={{ fontSize: 36 }}>☁️</span>
                    <p>{file ? 'Ready to upload. Click Upload Now.' : 'Select a ZIP file, then click Upload.'}</p>
                  </div>
                )}

                {uploading && (
                  <div style={styles.uploadingState}>
                    <div style={styles.progressBar}>
                      <div style={{ ...styles.progressFill, width: `${Math.round(progress)}%` }} />
                    </div>
                    <div style={styles.progressLabel}>{Math.round(progress)}% — Uploading and extracting…</div>
                  </div>
                )}

                {status === UPLOAD_STATES.error && (
                  <div style={styles.idleState}>
                    <span style={{ fontSize: 36 }}>❌</span>
                    <p style={{ color: 'var(--error)' }}>Upload failed. Please try again.</p>
                  </div>
                )}
              </div>
            )}

            {/* Post-upload: success banner + extracted file tree */}
            {done && result && (
              <>
                <div className="card" style={styles.successBanner}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>✅</span>
                    <div style={{ flex: 1 }}>
                      <div style={styles.successTitle}>Upload complete!</div>
                      <div style={styles.successMeta}>
                        {result.filesExtracted ?? 0} files extracted
                        {result.targetPath ? ` → ${result.targetPath}` : ''}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
                      onClick={() => navigate('../files')}
                    >
                      View in Project Files →
                    </button>
                  </div>
                </div>

                {showExtractedTree && (
                  <div className="card" style={styles.extractedTreeCard}>
                    <div style={styles.treeHeader}>
                      <span style={{ fontSize: 16 }}>📂</span>
                      <span style={styles.treeTitle}>Extracted Files</span>
                      {zipStats && (
                        <span style={styles.treeStats}>
                          {zipStats.files} file{zipStats.files !== 1 ? 's' : ''}
                          {zipStats.folders > 0 ? `, ${zipStats.folders} folder${zipStats.folders !== 1 ? 's' : ''}` : ''}
                        </span>
                      )}
                    </div>
                    <div style={styles.extractedTreeBody}>
                      <FileTreeView node={zipTree} />
                    </div>
                  </div>
                )}
              </>
            )}

            {!done && (
              <div className="app-hints">
                <p><span>ℹ️</span> Only <strong>.zip</strong> archives are accepted. The contents are extracted into the project source folder on the server.</p>
                <p><span>🔁</span> Uploading again <strong>overwrites</strong> existing files.</p>
                <p><span>📂</span> Subfolders inside the ZIP are preserved during extraction.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const styles = {
  stepCard: { padding: 'var(--space-lg)' },
  stepHeader: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  stepNum: {
    width: 28, height: 28, borderRadius: '50%',
    background: 'var(--primary)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 800, flexShrink: 0,
  },
  stepTitle: { fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--font-size-base)', lineHeight: 1.3 },
  stepSub:   { fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 },

  dropZone: {
    border: '2px dashed var(--border)', borderRadius: 'var(--radius-md)',
    padding: '24px 16px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 6, cursor: 'pointer', marginTop: 12,
    transition: 'border-color 0.15s, background 0.15s',
    background: 'var(--bg-table-row-alt)',
  },
  dropLabel: { fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--font-size-base)' },
  dropSub:   { fontSize: 11, color: 'var(--text-muted)' },

  fileRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '5px 8px', background: 'var(--bg-disabled)',
    borderRadius: 6, fontSize: 12, marginTop: 10,
  },
  fileIcon: { flexShrink: 0 },
  fileName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)', fontWeight: 500 },
  fileSize: { color: 'var(--text-muted)', flexShrink: 0 },
  removeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-muted)', fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0,
  },
  zipError: {
    marginTop: 8, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
    background: 'var(--error-bg, #fef2f2)', color: 'var(--error, #dc2626)',
    fontSize: 12, fontWeight: 500,
  },

  treeCard: { padding: 0, overflow: 'hidden' },
  treeHeader: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    padding: '12px 16px', borderBottom: '1px solid var(--border)',
    background: 'var(--bg-table-row-alt)',
  },
  treeTitle: { fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)' },
  treeStats: { fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' },
  treeScroll: {
    maxHeight: 380, overflowY: 'auto', padding: '8px 0',
    fontFamily: 'var(--font-mono, monospace)',
  },

  statusCard: { padding: 'var(--space-lg)' },
  statusTitle: { fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, fontSize: 'var(--font-size-base)' },
  idleState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 10, padding: '24px 0', color: 'var(--text-muted)',
    fontSize: 'var(--font-size-sm)', textAlign: 'center',
  },
  uploadingState: { display: 'flex', flexDirection: 'column', gap: 10 },
  progressBar: {
    height: 10, background: 'var(--border)', borderRadius: 'var(--radius-pill)', overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: 'linear-gradient(90deg,var(--primary),var(--secondary))',
    borderRadius: 'var(--radius-pill)', transition: 'width 0.2s ease',
  },
  progressLabel: { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 },

  successBanner: {
    padding: '14px 16px',
    background: 'var(--success-bg)',
    border: '1.5px solid rgba(27,127,59,0.25)',
  },
  successTitle: { fontWeight: 700, color: 'var(--success)', fontSize: 'var(--font-size-base)' },
  successMeta:  { fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono, monospace)' },

  extractedTreeCard: { padding: 0, overflow: 'hidden', flex: 1 },
  extractedTreeBody: { padding: '10px 14px', maxHeight: 520, overflowY: 'auto' },
};

const treeStyles = {
  row: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '2px 8px', cursor: 'default',
    fontSize: 12, lineHeight: 1.6,
    transition: 'background 0.1s', userSelect: 'none',
  },
  arrow: { fontSize: 9, color: 'var(--text-muted)', width: 10, flexShrink: 0 },
  icon:  { fontSize: 13, flexShrink: 0 },
  label: { color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
